import { useWidgetContext } from 'widgets/WidgetContext';
import {
    VenueAnalysisData,
    VenueAnalysisVenueData,
} from 'widgets/venueAnalysis/venueAnalysisAggregatedData';
import { VenueAnalysisVenueDisplayData } from 'widgets/venueAnalysis/grid/gridData';
import { WidgetType } from 'widgets/WidgetType';
import { DropdownTooltipText } from '@instinet-analytics/semantic-ui-components';
import React from 'react';
import {
    WidgetStrategyWithConfig,
    WidgetStrategyWithStreamByRequestUid,
} from 'widgets/WidgetRegistry';
import {
    initialVenueAnalysisCapsuleConfigState,
    VenueAnalysisCapsuleConfigState,
} from 'widgets/venueAnalysis/capsules/store/VenueAnalysisCapsuleConfigState';
import { VenueAnalysisByOrderIdsOrSymbolStreamRequest } from 'widgets/venueAnalysis/api/VenueAnalysisByOrderIdsOrSymbolStreamRequest';
import { OrderFillsCapsule } from 'widgets/venueAnalysis/capsules/orderFillsCapsulesShared/components/OrderFillsCapsule';
import { WidgetCategory } from 'widgets/WidgetCategory';
import { venueAnalysisStreamConfig } from 'widgets/venueAnalysis/store/stream/stream-config';
import { PortfolioSelectionRequirements } from 'widgets/PortfolioSelectionRequirements';
import { OrderFillsCapsuleWidgetType } from 'widgets/venueAnalysis/capsules/orderFillsCapsulesShared/types/OrderFillsCapsuleWidgetType';
import { orderFillsByVenueTypeCapsuleStrategy } from 'widgets/venueAnalysis/capsules/orderFillsByVenueTypeCapsule/orderFillsByVenueTypeCapsuleStrategy';
import { orderFillsByVenueCapsuleStrategy } from 'widgets/venueAnalysis/capsules/orderFillsByVenueCapsule/orderFillsByVenueCapsuleStrategy';

export interface OrderFillsCapsuleStrategy {
    title: string;
    description: DropdownTooltipText;
    helpText: string;
    getOrderDisplayData: (
        data: VenueAnalysisData
    ) => VenueAnalysisVenueDisplayData[];
    renderLabel: (
        venue: VenueAnalysisVenueData,
        percentText: string
    ) => React.ReactNode;
}

function getOrderFillsCapsuleStrategy(widgetType: OrderFillsCapsuleWidgetType) {
    if (widgetType === WidgetType.ORDER_FILLS_BY_VENUE_CAPSULE) {
        return orderFillsByVenueCapsuleStrategy;
    }
    if (widgetType === WidgetType.ORDER_FILLS_BY_VENUE_TYPE_CAPSULE) {
        return orderFillsByVenueTypeCapsuleStrategy;
    }
    throw new Error('Unexpected widget type ' + widgetType);
}

export function useOrderFillsCapsuleStrategy(): OrderFillsCapsuleStrategy {
    const { widgetType } = useWidgetContext();
    return getOrderFillsCapsuleStrategy(
        widgetType as OrderFillsCapsuleWidgetType
    );
}

type OrderFillsCapsuleWidgetStrategy =
    WidgetStrategyWithConfig<VenueAnalysisCapsuleConfigState> &
        WidgetStrategyWithStreamByRequestUid<VenueAnalysisByOrderIdsOrSymbolStreamRequest>;

export const orderFillsCapsuleWidgetStrategy: (
    widgetType: OrderFillsCapsuleWidgetType
) => OrderFillsCapsuleWidgetStrategy = (
    widgetType: OrderFillsCapsuleWidgetType
) => {
    const capsuleStrategy = getOrderFillsCapsuleStrategy(widgetType);
    const { title, description } = capsuleStrategy;

    return {
        title: title,
        supportedHosts: ['tile'],
        reactComponentType: OrderFillsCapsule,
        categories: [WidgetCategory.FILL_CAPSULES],
        description: description,
        managedStreamConfig: venueAnalysisStreamConfig,
        portfolioSelectionRequirements:
            PortfolioSelectionRequirements.PositionFromAtLeastOneOrder,
        initialConfigState: initialVenueAnalysisCapsuleConfigState,
    };
};
